import { Button } from "components/Button/Button";
import { InputComponentProps } from "components/Form/components/InputComponentProps";
import { Password } from "components/Form/components/Password/Password";
import { Textarea } from "components/Form/components/Textarea/Textarea";
import { IconCollection } from "components/Icon/IconCollection";
import { useEffect, useRef, useState } from "react";
import "./WriteOnlyInput.scss";

// "Write key" -> "write key" for "Set write key"; acronyms ("API key") keep their case.
export const getWriteOnlyButtonNoun = (label: string | undefined, identifier: string): string => {
    if (!label) {
        return identifier;
    }
    const startsWithAcronym = label.length > 1 && label[1] === label[1].toUpperCase();
    return startsWithAcronym ? label : label.charAt(0).toLowerCase() + label.slice(1);
};

export type WriteOnlyInputProps = {
    identifier: string;
    /** The field label; names the field on the button ("Set write key"). */
    label?: string;
    value: any;
    attributes?: any; // TODO consider improvement
    touched: boolean;
    errorMessage: string;
    hasValue: boolean;
    /** The stored secret reference (e.g. `vault.secret(...)`), shown next to the button. */
    secretReference?: string;
    isMultiline: boolean;
    isFormSubmitted: boolean;
    input: InputComponentProps<string>;
};

export const WriteOnlyInput = ({
    identifier,
    label: fieldLabel,
    value,
    attributes,
    touched,
    errorMessage,
    hasValue,
    secretReference,
    isMultiline,
    isFormSubmitted,
    input,
}: WriteOnlyInputProps) => {
    const [isFieldOpened, setFieldOpened] = useState(false);
    const inputRef = useRef<any>();

    const changeToInputField = () => {
        setFieldOpened(true);
    };
    const changeToButton = () => {
        setFieldOpened(false);
    };

    useEffect(() => {
        if (isFormSubmitted) {
            changeToButton();
        }
    }, [isFormSubmitted]);

    useEffect(() => {
        if (isFieldOpened && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isFieldOpened]);

    const label = `${hasValue ? "Change" : "Set"} ${getWriteOnlyButtonNoun(
        fieldLabel,
        identifier
    )}`;

    return isFieldOpened ? (
        isMultiline ? (
            <Textarea
                name={identifier}
                inputProps={{ ...input, value: value }}
                attributes={attributes}
                inputRef={inputRef}
            />
        ) : (
            <div className="inputField">
                <Password
                    input={input}
                    attributes={attributes}
                    name={identifier}
                    touched={touched}
                    errorMessage={errorMessage}
                    inputRef={inputRef}
                />
            </div>
        )
    ) : (
        <div className="writeOnlyInput">
            <Button
                onClick={changeToInputField}
                icon={IconCollection.key}
                variation="dark"
                label={label}
            />
            {secretReference ? (
                <span className="writeOnlyInput__reference" title={secretReference}>
                    Reference: {secretReference}
                </span>
            ) : null}
        </div>
    );
};
